// Page metadata is produced in two places that must agree: the server injects
// it into the app shell for the initial request (see
// server/src/node/openGraph.ts), and the client re-declares it on every
// client-side navigation (see web/src/pageMetadata.ts).

export type PageMetadata = {
  title?: string,
  description?: string,
  imagePath?: string,
  imageAlt?: string,
};

export const DEFAULT_PAGE_METADATA: Required<PageMetadata> = {
  title: 'Test analytics for GitHub and GitLab · Flakiness.io',
  description: 'Track test performance across commits to detect flaky tests and regressions.',
  imagePath: '/og-image-v1.png',
  imageAlt: 'Flakiness.io',
};

// Metadata for a page inside an organization or a project. `section` names what
// the page shows within it, such as `Runs` or `Run #123: nightly-webkit`, and
// `testsCount` labels the project's own page. Sections that require access are
// never shared or crawled, so they keep the default description.
export function pageMetadata(options: {
  orgSlug: string,
  projectSlug?: string,
  section?: string,
  testsCount?: number,
  accessGated?: boolean,
}): PageMetadata {
  const name = options.projectSlug ? `${options.orgSlug}/${options.projectSlug}` : options.orgSlug;
  const count = options.testsCount ?? 0;
  const testsLabel = count > 0 ? ` · ${count.toLocaleString('en-US')} ${count === 1 ? 'test' : 'tests'}` : '';
  const title = options.section ? `${options.section} · ${name}` : `${name}${testsLabel}`;
  if (options.accessGated)
    return { title };
  return {
    title,
    description: `Browse ${name} test analytics on Flakiness.io.`,
    imageAlt: `Flakiness.io ${options.projectSlug ? 'project' : 'organization'} ${name}`,
  };
}
